import { type EquipSlot, ITEMS, type Rarity } from '@nine/shared';
import { cloud, flames, sabre, sprite, star, sword, blade } from '../kit';
import { C, type Hex, mix, type Pix } from '../pix';
import { defineIconKind, registerIcons } from '../store';

/**
 * Equipment (อาวุธและเครื่องแต่งกาย): the tile is the item's rarity (common stone, rare blue, epic
 * orange, legend red), the picture the piece itself. Items with no picture of their own are drawn
 * from their look and slot (`@look:slot:rarity` ids, see `itemIconId` in render/icons.ts).
 */

/** Glow behind an item, by rarity. */
export const RARITY_COLORS: Readonly<Record<Rarity, string>> = {
  common: '#b8ae9c',
  rare: '#5aa8ff',
  epic: '#ff8a4a',
  legend: '#ff5a3a',
  fine: '#7ed07a',
  mythic: '#ffd24a',
};

const parse = (id: string): { visual: string; slot: EquipSlot; rarity: Rarity } => {
  const it = ITEMS[id];
  if (it) return it;
  const [visual = 'steel', slot = 'weapon', rarity = 'common'] = id.replace(/^@/, '').split(':');
  return { visual, slot: slot as EquipSlot, rarity: rarity as Rarity };
};

// ── pieces ──

function jian(p: Pix, blade: Hex, hilt: Hex, tassel?: Hex): void {
  sword(p, 6, 26, 26, 6, { blade, hilt, tassel, w: 2.6 });
}

/** A conical bamboo hat. */
function douli(p: Pix, col: Hex, band: Hex = C.woodDark): void {
  for (let y = 0; y < 10; y++)
    p.rect(
      Math.round(15.5 - y * 1.4),
      8 + y,
      Math.round(y * 2.8 + 2),
      1,
      y % 3 === 0 ? mix(col, C.white, 0.3) : col,
    );
  p.rect(2, 18, 28, 2, mix(col, C.ink, 0.3));
  p.rect(9, 16, 14, 1, band);
  p.line(10, 20, 13, 27, C.woodDark).line(22, 20, 19, 27, C.woodDark);
}

/** A cloth headband knotted with two trailing tails. */
function headband(p: Pix, col: Hex): void {
  p.ell(15, 16, 10, 7, '#3a2a28');
  p.ell(15, 14, 8, 5, '#2a1e20');
  p.rect(5, 13, 20, 4, col);
  p.rect(23, 14, 4, 4, mix(col, C.ink, 0.3));
  p.thick(26, 17, 29, 26, col, 2);
  p.thick(25, 17, 23, 27, mix(col, C.ink, 0.2), 2);
}

/** An iron helmet with a padded cloth rim. */
function helm(p: Pix): void {
  p.ell(16, 16, 10, 10, C.steelMid);
  for (let y = 16; y < 27; y++) for (let x = 5; x < 27; x++) p.erase(x, y);
  p.rect(6, 16, 20, 4, '#8a5a3a');
  p.rect(15, 5, 2, 11, C.steelDark);
  p.rect(14, 3, 4, 3, C.gold);
  p.rect(8, 20, 3, 6, '#8a5a3a').rect(21, 20, 3, 6, '#8a5a3a');
}

/** A crown: a gold band with three jade peaks. */
function crown(p: Pix, jade: Hex, gold: Hex = C.gold): void {
  p.rect(6, 18, 20, 6, gold);
  p.rect(6, 18, 20, 1, C.goldLight);
  for (const [x, h] of [
    [8, 6],
    [14, 10],
    [20, 6],
  ] as const) {
    for (let k = 0; k < h; k++) p.rect(x + 2 - Math.floor(k / 3), 18 - k, 1 + Math.floor(k / 3) * 2, 1, gold);
    p.ell(x + 2.5, 17 - h, 2.6, 2.6, jade);
    p.px(x + 3, 16 - h, C.white);
  }
  p.rect(14, 20, 4, 2, jade);
}

/** A wrapped turban with a jewel. */
function turban(p: Pix, col: Hex): void {
  p.ell(16, 16, 11, 8, col);
  for (let k = 0; k < 4; k++) p.line(6 + k * 2, 21 - k * 3, 26 - k, 13 + k * 2, mix(col, C.ink, 0.25));
  p.ell(16, 13, 2.5, 2.5, '#c8402a');
  p.px(15, 12, C.white);
  p.rect(7, 22, 18, 3, mix(col, C.ink, 0.15));
}

/** A long coat seen from the front: sleeves out, collar crossed, belt. */
function coat(p: Pix, col: Hex, o: { trim?: Hex; belt?: Hex; fur?: boolean; scales?: boolean } = {}): void {
  for (let y = 0; y < 21; y++) p.rect(10 - Math.floor(y / 4), 6 + y, 12 + Math.floor(y / 4) * 2, 1, col);
  p.thick(10, 7, 3, 19, col, 4);
  p.thick(22, 7, 29, 19, col, 4);
  p.line(12, 6, 17, 15, o.trim ?? mix(col, C.white, 0.4));
  p.line(20, 6, 16, 11, mix(col, C.ink, 0.4));
  p.rect(8, 16, 16, 2, o.belt ?? C.gold);
  p.line(16, 18, 16, 26, mix(col, C.ink, 0.35));
  if (o.trim) {
    p.rect(5, 26, 22, 1, o.trim);
    p.px(3, 19, o.trim).px(29, 19, o.trim);
  }
  if (o.fur) for (let x = 5; x < 27; x += 2) p.rect(x, 26 + (x % 4 ? 0 : 1), 2, 1, '#e8dcc0');
  if (o.scales)
    for (let y = 8; y < 26; y += 3)
      for (let x = 9 - Math.floor(y / 6); x < 24; x += 3) p.px(x + (y % 2), y, mix(col, C.white, 0.35));
}

function generated(p: Pix, slot: EquipSlot, tint: Hex): void {
  if (slot === 'weapon') jian(p, C.steel, tint);
  else if (slot === 'head') headband(p, tint);
  else coat(p, tint);
}

const ITEM_TINT: Readonly<Record<string, Hex>> = {
  steel: '#9aa6b8',
  azure: '#4a90e8',
  crimson: '#d8303a',
  demonblade: '#a0202a',
  band: '#c8443b',
  douli: '#b8964e',
  jadeCrown: '#4fc49a',
  coatNavy: '#34558e',
  pelt: '#8a6a48',
  coatBlack: '#3a3442',
  coatCrimson: '#a8302e',
};

defineIconKind('item', {
  label: 'อาวุธและเครื่องแต่งกาย',
  ids: () => Object.keys(ITEMS),
  name: (id) => ITEMS[id]?.name ?? id,
  tint: (id) => RARITY_COLORS[parse(id).rarity] ?? RARITY_COLORS.common,
  plate: 'square',
  fallback: (id) => {
    const it = parse(id);
    return {
      draw: (p) => generated(p, it.slot, ITEM_TINT[it.visual] ?? RARITY_COLORS[it.rarity]),
    };
  },
});

registerIcons('item', {
  'iron-sword': (p) => jian(p, C.steel, C.steelDark),
  'smith-sword': (p) => {
    jian(p, '#c8ccd4', C.wood);
    for (const [x, y] of [
      [14, 17],
      [18, 13],
    ] as const)
      p.px(x, y, C.steelDark);
  },
  'smith-blade': (p) => jian(p, '#6a7080', '#3a3a44', '#8a9098'),
  'azure-sword': (p) => {
    jian(p, '#b8dcff', '#2a70d8', '#5aa8ff');
    p.line(9, 21, 24, 7, '#5aa8ff');
  },
  'crimson-sword': (p) => jian(p, '#ffc8c0', '#c82a32', '#ff5a3a'),
  'heaven-sword': (p) => {
    cloud(p, 3, 28, 14, C.white);
    jian(p, '#d8ecff', C.gold, '#5aa8ff');
    star(p, 25, 5, 2, C.white);
  },
  'hell-sabre': (p) => {
    flames(p, 4, 29, 24, 9, '#c8202a', '#ff8a4a');
    sabre(p, 6, 26, 26, 5, { blade: '#ffb0a8', hilt: '#2a1a1a', w: 4 });
  },
  'founder-sabre': (p) => {
    for (let k = 0; k < 6; k++) {
      const a = (k * Math.PI) / 3;
      p.line(
        16 + Math.cos(a) * 9,
        16 + Math.sin(a) * 9,
        16 + Math.cos(a) * 13,
        16 + Math.sin(a) * 13,
        '#ff5a3a',
      );
    }
    blade(p, 10, 22, 27, 4, { w: 3.4, col: '#3a2a34', edge: '#ff5a5a' });
    p.thick(6, 26, 10, 22, '#1a1016', 2);
    p.thick(7, 19, 13, 25, '#c8202a', 2);
    for (const [x, y] of [
      [16, 15],
      [20, 11],
      [23, 8],
    ] as const)
      p.px(x, y, '#ff8a8a');
  },
  'red-band': (p) => headband(p, '#d8403a'),
  'smith-helm': (p) => helm(p),
  'bamboo-hat': (p) => douli(p, '#c8a45a'),
  'tailor-hat': (p) => douli(p, '#e0c878', '#c8402a'),
  'jade-crown': (p) => crown(p, '#4fc49a'),
  'heaven-crown': (p) => {
    cloud(p, 4, 29, 24, C.white);
    crown(p, '#7ae0ff');
  },
  'tailor-turban': (p) => turban(p, '#d8c8a0'),
  'navy-coat': (p) => coat(p, '#34558e', { trim: C.gold }),
  'tailor-robe': (p) => coat(p, '#3a5aa8', { belt: '#e8dcc0' }),
  'wolf-pelt': (p) => coat(p, '#8a6a48', { fur: true, belt: '#5a3a28' }),
  'black-vest': (p) => coat(p, '#3a3442', { belt: '#8a7a6a' }),
  'smith-mail': (p) => coat(p, '#5a6070', { scales: true, belt: '#8a5a3a' }),
  'tailor-silk': (p) => coat(p, '#3a2a5a', { trim: '#b07aff', belt: '#c8a8f0' }),
  'crimson-coat': (p) => coat(p, '#a8302e', { trim: C.gold, belt: C.ink }),
  'hell-coat': (p) => {
    coat(p, '#2a2430', { trim: '#ff5a3a', belt: '#c8202a' });
    sprite(p, 9, 21, ['.r..r..r..r..r', 'rrr.rr.rrr.rrr'], { r: '#ff5a3a' });
  },
});
